import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";
import {
  adminDeleteCategory,
  adminReorderCategories,
  adminUpsertCategory,
  type CategoryInput,
} from "@/lib/callables";
import type { Category } from "@/types";
import { fetchCollection } from "./use-firestore";

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories(),
    queryFn: () => fetchCollection<Category>("categories"),
    // A ordenação é aqui pelo mesmo motivo dos vendedores: documento sem o
    // campo `order` sumiria de uma consulta com `orderBy`, e uma categoria
    // invisível no painel é a pior forma de erro possível numa lista que
    // existe justamente para gerenciá-las.
    select: (rows) =>
      [...rows].sort((a, b) => {
        const byOrder = (a.order ?? 999) - (b.order ?? 999);
        if (byOrder !== 0) return byOrder;
        return (a.name ?? "").localeCompare(b.name ?? "", "pt-BR");
      }),
    // Categorias são fixas do projeto e mudam raramente.
    staleTime: 5 * 60_000,
  });
}

export function useUpsertCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CategoryInput) => adminUpsertCategory(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.categories() }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminDeleteCategory({ id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.categories() }),
  });
}

export function useReorderCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order: { id: string; order: number }[]) => adminReorderCategories({ order }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.categories() }),
  });
}
